import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Paper,
  Chip,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const IvrDecisionModal = ({ open, onWaitForVerification, onContinueNow }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const surface = isDark ? "#0F172A" : "#ffffff";
  const border = isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.10)";
  const mutedText = isDark ? "rgba(226,232,240,0.65)" : "rgba(15,23,42,0.55)";
  const titleText = isDark ? "#E2E8F0" : "#0F172A";
  const noteBg = isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)";

  const noteBox = {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    background: noteBg,
    borderRadius: 1.5,
    p: "8px 10px",
    mt: 1,
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: surface,
          border: `1px solid ${border}`,
          boxShadow: isDark
            ? "0 24px 64px rgba(0,0,0,0.6)"
            : "0 24px 64px rgba(2,6,23,0.18)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${border}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: "success.light",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "success.main" }} />
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: titleText, lineHeight: 1.2 }}>
            IVR submitted successfully
          </Typography>
          <Typography variant="body2" sx={{ color: mutedText, mt: 0.25 }}>
            How would you like to proceed with this order?
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>

          {/* Option 1 — recommended */}
          <Paper
            elevation={0}
            onClick={onWaitForVerification}
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: `1px solid ${border}`,
              cursor: "pointer",
              transition: "border-color 0.15s ease",
              "&:hover": { borderColor: "info.main" },
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: "info.lighter",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <HourglassEmptyIcon sx={{ fontSize: 18, color: "info.main" }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: titleText }}>
                    Submit for IVR approval
                  </Typography>
                  <Chip label="Recommended" size="small" color="success" sx={{ fontSize: 11, height: 20 }} />
                </Box>
                <Typography variant="body2" sx={{ color: mutedText, lineHeight: 1.6 }}>
                  We will run through it and keep you posted.
                </Typography>
                <Box sx={noteBox}>
                  <Typography variant="caption" sx={{ color: mutedText, lineHeight: 1.55 }}>
                    <strong style={{ color: titleText }}>Note:</strong> Come back and place the order once the IVR is approved.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Option 2 */}
          <Paper
            elevation={0}
            onClick={onContinueNow}
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: `1px solid ${border}`,
              cursor: "pointer",
              transition: "border-color 0.15s ease",
              "&:hover": { borderColor: "warning.main" },
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: "warning.lighter",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 18, color: "warning.main" }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: titleText }}>
                    Do not wait - place order now
                  </Typography>
                  <Chip label="Faster flow" size="small" color="warning" sx={{ fontSize: 11, height: 20 }} />
                </Box>
                <Typography variant="body2" sx={{ color: mutedText, lineHeight: 1.6 }}>
                  Skip IVR verification and proceed to kit selection immediately.
                </Typography>
                <Box sx={noteBox}>
                  <Typography variant="caption" sx={{ color: mutedText, lineHeight: 1.55 }}>
                    <strong style={{ color: titleText }}>Note:</strong> Only if you have your own DME license.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

        </Box>
      </DialogContent>
    </Dialog>
  );
};


export default IvrDecisionModal;
